import { useState } from 'react';
import DoorVisual from './DoorVisual';
import { doorDesigns, doorColours, glassStyles, DoorDesign, DoorColour, GlassStyle } from '../../../mocks/compositeDoors';

interface DoorCatalogueProps {
  onRequestQuote: (doorName: string, colourName: string) => void;
}

const collectionConfig: Record<string, { label: string; desc: string; badge: string; badgeClass: string }> = {
  all: {
    label: 'All Designs',
    desc: 'Browse all 26 composite door designs across Traditional, Modern, Cottage & Country, and Signature Deluxe collections.',
    badge: '',
    badgeClass: '',
  },
  traditional: {
    label: 'Traditional',
    desc: 'Classic raised panel doors in the Georgian tradition — beautifully proportioned, endlessly versatile, and universally loved.',
    badge: 'Traditional',
    badgeClass: 'bg-amber-100 text-amber-700',
  },
  modern: {
    label: 'Modern',
    desc: 'Contemporary composite doors with clean lines, minimal detail, and urban confidence. Perfect for new-builds and modern extensions.',
    badge: 'Modern',
    badgeClass: 'bg-zinc-100 text-zinc-600',
  },
  cottage: {
    label: 'Cottage & Country',
    desc: 'Inspired by rural English architecture — grooved boards, cottage glazing, and honest character for homes with warmth and charm.',
    badge: 'Cottage & Country',
    badgeClass: 'bg-stone-100 text-stone-600',
  },
  deluxe: {
    label: 'Signature Deluxe',
    desc: 'The pinnacle of Hallmark composite door engineering. HallMAX PRO 70mm double-rebate frame, premium GRP skins, and exclusive Deluxe Matte colours.',
    badge: 'Signature Deluxe',
    badgeClass: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
  },
};

const colourCategoryLabels: Record<string, string> = {
  standard: 'Standard Colours',
  woodgrain: 'Woodgrain Finishes',
  'deluxe-matte': 'Deluxe Matte (New 2025)',
};

const glassCategoryLabels: Record<string, string> = {
  solid: 'Solid',
  modern: 'Modern & Sandblasted',
  traditional: 'Traditional & Decorative',
  privacy: 'Privacy Glass',
};

export default function DoorCatalogue({ onRequestQuote }: DoorCatalogueProps) {
  const [activeCollection, setActiveCollection] = useState<string>('all');
  const [selectedDoor, setSelectedDoor] = useState<DoorDesign>(doorDesigns[0]);
  const [selectedColour, setSelectedColour] = useState<DoorColour>(doorColours[2]);
  const [selectedGlass, setSelectedGlass] = useState<GlassStyle>(glassStyles[4]);

  const filtered =
    activeCollection === 'all'
      ? doorDesigns
      : doorDesigns.filter((d) => d.collection === activeCollection);

  const coloursByCategory = doorColours.reduce<Record<string, DoorColour[]>>((acc, c) => {
    if (!acc[c.category]) acc[c.category] = [];
    acc[c.category].push(c);
    return acc;
  }, {});

  const glassByCategory = glassStyles.reduce<Record<string, GlassStyle[]>>((acc, g) => {
    if (!acc[g.category]) acc[g.category] = [];
    acc[g.category].push(g);
    return acc;
  }, {});

  const cfg = collectionConfig[activeCollection];

  return (
    <section id="door-catalogue" className="py-20 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-10">
          <span
            className="text-amber-600 text-xs font-bold tracking-widest uppercase"
            style={{ fontFamily: "'Raleway', sans-serif" }}
          >
            Hallmark Door Range
          </span>
          <h2
            className="text-4xl font-bold text-zinc-900 mt-3 mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Browse the Full Hallmark Catalogue
          </h2>
          <p className="text-zinc-500 text-sm leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>
            We supply and install doors from the full Hallmark composite range — Traditional, Modern, Cottage &amp; Country,
            and Signature Deluxe. Choose a design, pick your colour and glass, then request your free survey.
          </p>
        </div>

        {/* Collection Tabs */}
        <div className="flex flex-wrap gap-2 justify-center mb-3">
          {Object.keys(collectionConfig).map((key) => (
            <button
              key={key}
              onClick={() => setActiveCollection(key)}
              className={`px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
                activeCollection === key
                  ? 'bg-zinc-900 text-white'
                  : 'bg-white border border-stone-200 text-zinc-600 hover:border-zinc-400'
              }`}
              style={{ fontFamily: "'Raleway', sans-serif" }}
            >
              {collectionConfig[key].label}
              {key === 'deluxe' && (
                <span className="ml-1.5 text-[9px] uppercase tracking-widest text-amber-500">★</span>
              )}
            </button>
          ))}
        </div>
        <p
          className="text-center text-zinc-400 text-xs mb-10 max-w-xl mx-auto"
          style={{ fontFamily: "'Raleway', sans-serif" }}
        >
          {cfg.desc}
        </p>

        {/* Door Grid + Configurator */}
        <div className="grid lg:grid-cols-3 gap-8 items-start">
          {/* Door Grid */}
          <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-4">
            {filtered.map((door) => {
              const isSelected = selectedDoor.id === door.id;
              const collCfg = collectionConfig[door.collection];
              return (
                <button
                  key={door.id}
                  onClick={() => setSelectedDoor(door)}
                  className={`group text-left rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                    isSelected
                      ? 'border-amber-500 ring-2 ring-amber-200'
                      : 'border-stone-200 hover:border-zinc-400 bg-white'
                  }`}
                >
                  <div className="relative bg-stone-100 p-4 pb-3 flex justify-center">
                    <div style={{ width: '90px', height: '130px' }}>
                      <DoorVisual
                        panelType={door.panelType}
                        colorHex={isSelected ? selectedColour.hex : '#3D4446'}
                        isLight={isSelected ? selectedColour.isLight : false}
                        glassId={isSelected ? selectedGlass.id : 'classic-diamond'}
                      />
                    </div>
                    <div className="absolute top-2 left-2 flex flex-col gap-1">
                      {door.popular && (
                        <span
                          className="bg-amber-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide"
                          style={{ fontFamily: "'Raleway', sans-serif" }}
                        >
                          Best Seller
                        </span>
                      )}
                      {door.newDesign && (
                        <span
                          className="bg-zinc-800 text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide"
                          style={{ fontFamily: "'Raleway', sans-serif" }}
                        >
                          New
                        </span>
                      )}
                    </div>
                    {activeCollection === 'all' && collCfg.badge && (
                      <span
                        className={`absolute bottom-2 right-2 text-[8px] font-bold px-1.5 py-0.5 rounded-full ${collCfg.badgeClass}`}
                        style={{ fontFamily: "'Raleway', sans-serif" }}
                      >
                        {collCfg.badge}
                      </span>
                    )}
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-5 h-5 flex items-center justify-center bg-amber-500 rounded-full">
                        <i className="ri-check-line text-white text-xs"></i>
                      </div>
                    )}
                  </div>
                  <div className="p-3 bg-white">
                    <p
                      className="font-bold text-zinc-800 text-sm"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    >
                      {door.name}
                    </p>
                    <p
                      className="text-zinc-400 text-[11px] mt-0.5 leading-snug"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    >
                      {door.tagline}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Configurator Panel */}
          <div className="lg:sticky lg:top-28 bg-white border border-stone-200 rounded-xl p-6 space-y-5">
            {/* Selected door preview */}
            <div className="flex gap-4 items-start">
              <div style={{ width: '72px', height: '104px', flexShrink: 0 }}>
                <DoorVisual
                  panelType={selectedDoor.panelType}
                  colorHex={selectedColour.hex}
                  isLight={selectedColour.isLight}
                  glassId={selectedGlass.id}
                />
              </div>
              <div>
                <span
                  className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
                    collectionConfig[selectedDoor.collection].badgeClass || 'bg-stone-100 text-stone-600'
                  }`}
                  style={{ fontFamily: "'Raleway', sans-serif" }}
                >
                  {collectionConfig[selectedDoor.collection].label}
                </span>
                <h3
                  className="text-xl font-bold text-zinc-900 mt-1.5 mb-1"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {selectedDoor.name}
                </h3>
                <p
                  className="text-zinc-400 text-xs leading-relaxed"
                  style={{ fontFamily: "'Raleway', sans-serif" }}
                >
                  {selectedDoor.tagline}
                </p>
              </div>
            </div>

            {/* Colour Selector */}
            <div>
              <p
                className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-3"
                style={{ fontFamily: "'Raleway', sans-serif" }}
              >
                Colour: <span className="text-zinc-800 normal-case font-semibold">{selectedColour.name}</span>
                {selectedColour.category === 'deluxe-matte' && (
                  <span className="ml-2 text-yellow-600 text-[9px] font-bold">Deluxe Matte</span>
                )}
              </p>
              <div className="space-y-2">
                {(['standard', 'woodgrain', 'deluxe-matte'] as const).map((cat) => (
                  <div key={cat}>
                    <p
                      className="text-[9px] uppercase tracking-widest text-zinc-400 mb-1.5"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    >
                      {colourCategoryLabels[cat]}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(coloursByCategory[cat] || []).map((c) => (
                        <button
                          key={c.name}
                          title={c.name}
                          onClick={() => setSelectedColour(c)}
                          className="cursor-pointer transition-transform hover:scale-110 flex-shrink-0"
                          style={{
                            width: '22px',
                            height: '22px',
                            borderRadius: '50%',
                            backgroundColor: c.hex,
                            border:
                              selectedColour.name === c.name
                                ? '3px solid #d97706'
                                : c.isLight
                                ? '2px solid #d1d5db'
                                : '2px solid transparent',
                            outline: selectedColour.name === c.name ? '2px solid #fde68a' : 'none',
                            outlineOffset: '1px',
                          }}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Glass Selector */}
            <div>
              <p
                className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-3"
                style={{ fontFamily: "'Raleway', sans-serif" }}
              >
                Glass: <span className="text-zinc-800 normal-case font-semibold">{selectedGlass.name}</span>
              </p>
              <div className="space-y-2">
                {(['solid', 'modern', 'traditional', 'privacy'] as const).map((cat) => (
                  <div key={cat}>
                    <p
                      className="text-[9px] uppercase tracking-widest text-zinc-400 mb-1.5"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    >
                      {glassCategoryLabels[cat]}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {(glassByCategory[cat] || []).map((g) => (
                        <button
                          key={g.id}
                          onClick={() => setSelectedGlass(g)}
                          title={g.desc}
                          className={`px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                            selectedGlass.id === g.id
                              ? 'bg-amber-600 border-amber-600 text-white'
                              : 'border-stone-200 text-zinc-600 hover:border-zinc-400 bg-white'
                          }`}
                          style={{ fontFamily: "'Raleway', sans-serif" }}
                        >
                          {g.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <p
                className="text-zinc-400 text-[11px] mt-2 leading-relaxed"
                style={{ fontFamily: "'Raleway', sans-serif" }}
              >
                {selectedGlass.desc}
              </p>
            </div>

            {/* Door description */}
            <p
              className="text-zinc-500 text-xs leading-relaxed border-t border-stone-100 pt-4"
              style={{ fontFamily: "'Raleway', sans-serif" }}
            >
              {selectedDoor.description}
            </p>

            {/* CTA */}
            <button
              onClick={() => onRequestQuote(selectedDoor.name, selectedColour.name)}
              className="w-full bg-amber-600 hover:bg-amber-500 text-white font-bold py-3.5 rounded-md transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              style={{ fontFamily: "'Raleway', sans-serif" }}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-price-tag-3-line"></i>
              </div>
              Request Quote for {selectedDoor.name}
            </button>
            <p
              className="text-center text-zinc-400 text-[11px] -mt-3"
              style={{ fontFamily: "'Raleway', sans-serif" }}
            >
              Free survey &amp; written quote within 48 hours — no obligation
            </p>
          </div>
        </div>

        {/* Hallmark Brand Note */}
        <div className="mt-14 bg-white border border-stone-200 rounded-xl p-7 flex flex-col md:flex-row items-start md:items-center gap-5">
          <div className="w-12 h-12 flex items-center justify-center bg-amber-50 rounded-xl border border-amber-100 flex-shrink-0">
            <i className="ri-award-fill text-amber-600 text-2xl"></i>
          </div>
          <div className="flex-1">
            <p
              className="text-zinc-800 font-bold text-sm mb-1"
              style={{ fontFamily: "'Raleway', sans-serif" }}
            >
              Authorised Hallmark Doors Installer — Voted UK&apos;s No.1 Door Manufacturer 5 Years in a Row
            </p>
            <p
              className="text-zinc-500 text-xs leading-relaxed"
              style={{ fontFamily: "'Raleway', sans-serif" }}
            >
              Every composite door we fit is manufactured by Hallmark Doors &amp; Panels Ltd — the UK&apos;s leading GRP
              composite door manufacturer and winner of Best Brand five consecutive years. Hallmark doors are trusted in
              over 500,000 homes across the UK and are backed by independently verified customer reviews on Trustpilot and Google.
              When you choose Ensure Windows, you get a Hallmark door installed by a local Surrey team that stands behind its work.
            </p>
          </div>
          <div className="flex flex-col gap-1.5 text-center flex-shrink-0">
            <span
              className="text-xs font-bold text-amber-700 uppercase tracking-wide"
              style={{ fontFamily: "'Raleway', sans-serif" }}
            >
              HallMAX PRO Available
            </span>
            <span
              className="text-[10px] text-zinc-400"
              style={{ fontFamily: "'Raleway', sans-serif" }}
            >
              70mm Twin Double-Rebate
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
